/**
 * Turning a validation report into things a person can read.
 *
 * THE BACKEND OWNS THE PROSE. Every `IssueOut.message` is customer-facing copy
 * written by the server, complete with what to do about it, and this file does
 * not paraphrase, shorten or replace it. What it adds is the scannable shell
 * around that prose: a three-word heading so a list of five issues can be
 * skimmed, and a "which pages" line, because `[3, 5]` is data and "Pages 3 and
 * 5" is English.
 */
import type { IssueOut, IssueSeverity, ValidationReport, Verdict } from '@core/lib/api/types';
import type { Severity } from '@core/components/ui';
import { formatCount } from '@core/lib/format';

/**
 * Short headings for the codes the validator can emit
 * (backend/app/services/pdf/issues.py::IssueCode).
 *
 * An unknown code falls back to a severity-shaped heading rather than the code
 * itself: "MARGIN_ENCROACHMENT_V2" is a support ticket, not a heading, and the
 * customer still gets the full server sentence underneath either way.
 */
const ISSUE_TITLES: Record<string, string> = {
  DAMAGED_FILE: 'We could not read this file',
  ENCRYPTED_FILE: 'This file is password protected',
  PRINT_RESTRICTED: 'This file does not allow printing',
  EMPTY_DOCUMENT: 'This file has no pages',
  MIXED_PAGE_SIZES: 'The pages are different sizes',
  PAGE_TOO_LARGE: 'Some pages are too big to print',
  MIXED_ORIENTATION: 'Some pages are sideways',
  NON_STANDARD_SIZE: 'This is an unusual paper size',
  HIGH_PAGE_COUNT: 'This is a large job',
  LOW_SCAN_DPI: 'The scan quality is low',
  MARGIN_ENCROACHMENT: 'Content sits close to the edge',
  NON_EMBEDDED_FONT: 'Some fonts are not stored in the file',
};

export function issueTitle(issue: IssueOut): string {
  const known = ISSUE_TITLES[issue.code];
  if (known) return known;
  return issue.severity === 'reject' ? 'We cannot print this file' : 'Something to check';
}

/** Maps the API's per-issue severity onto the UI kit's four-colour scale. */
export function issueSeverity(severity: IssueSeverity): Severity {
  if (severity === 'reject') return 'danger';
  if (severity === 'warn') return 'warning';
  return 'info';
}

/** Maps the document-level verdict onto the same scale. */
export function verdictSeverity(verdict: Verdict): Severity {
  if (verdict === 'reject') return 'danger';
  if (verdict === 'warn') return 'warning';
  return 'success';
}

/** How many page numbers to name before switching to "and N more" — matches the API. */
const MAX_PAGES_NAMED = 6;

/**
 * "Page 4", "Pages 2-3", "Pages 3 and 5", "Whole document".
 *
 * An EMPTY LIST MEANS THE WHOLE DOCUMENT, not "no pages" — the API says so
 * explicitly, and rendering it as "Pages: none" would be a lie about a damaged
 * file.
 */
export function formatPageScope(pages: readonly number[] | undefined): string {
  if (!pages || pages.length === 0) return 'Whole document';

  const sorted = [...new Set(pages)].sort((a, b) => a - b);
  if (sorted.length === 1) return `Page ${formatCount(sorted[0] as number)}`;

  // Collapse runs: [2,3,4,9] becomes "2-4 and 9", which is how a person would
  // say it and how the server's own prose reads.
  const runs: string[] = [];
  let runStart = sorted[0] as number;
  let previous = runStart;
  for (const page of sorted.slice(1)) {
    if (page === previous + 1) {
      previous = page;
      continue;
    }
    runs.push(runStart === previous ? `${runStart}` : `${runStart}-${previous}`);
    runStart = page;
    previous = page;
  }
  runs.push(runStart === previous ? `${runStart}` : `${runStart}-${previous}`);

  const shown = runs.slice(0, MAX_PAGES_NAMED);
  const hidden = runs.length - shown.length;
  const list =
    shown.length === 1
      ? (shown[0] as string)
      : `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1] as string}`;

  return hidden > 0 ? `Pages ${list} and ${formatCount(hidden)} more` : `Pages ${list}`;
}

/** "A4 (210 x 297 mm)", or the millimetres alone for a real but unnamed size. */
export function formatDetectedSize(report: ValidationReport): string {
  const { label, width_mm: width, height_mm: height } = report.detected_size;
  const millimetres =
    typeof width === 'number' && typeof height === 'number'
      ? `${Math.round(width)} × ${Math.round(height)} mm`
      : null;
  if (label && millimetres) return `${label} (${millimetres})`;
  if (label) return label;
  if (millimetres) return `${millimetres} (not a standard size)`;
  return 'Unknown';
}

/** The issues the customer is allowed to wave through. Rejects are never in here. */
export function acknowledgeableIssues(report: ValidationReport): IssueOut[] {
  return (report.issues ?? []).filter((issue) => issue.acknowledgeable);
}

/**
 * THE ACKNOWLEDGEMENT GATE, in one function.
 *
 * A report that asks for acknowledgement may not be carried forward until every
 * acknowledgeable warning has been ticked individually — not one blanket "I
 * agree", because the customer is confirming distinct facts ("my scan is
 * blurry", "this is 500 pages") and a single checkbox hides the second one
 * behind the first.
 *
 * A reject can never be acknowledged: the server marks those issues
 * `acknowledgeable: false`, so `pending` is non-empty and no tick can empty it.
 */
export function canProceed(report: ValidationReport, acknowledged: ReadonlySet<string>): boolean {
  if (report.verdict === 'reject') return false;
  if (!report.requires_acknowledgement) return true;
  return acknowledgeableIssues(report).every((issue) => acknowledged.has(issue.code));
}

/** How many ticks are still missing. Drives the "1 of 2 confirmed" hint. */
export function outstandingAcknowledgements(
  report: ValidationReport,
  acknowledged: ReadonlySet<string>,
): number {
  if (!report.requires_acknowledgement) return 0;
  return acknowledgeableIssues(report).filter((issue) => !acknowledged.has(issue.code)).length;
}

/** One sentence for the aria-live region when a report lands. */
export function verdictAnnouncement(report: ValidationReport): string {
  const issues = report.issues ?? [];
  const count = issues.length;
  const pages = `${formatCount(report.page_count)} ${report.page_count === 1 ? 'page' : 'pages'}`;

  if (report.verdict === 'reject') {
    return `We cannot print this file. ${count === 1 ? '1 problem' : `${formatCount(count)} problems`} found. Please upload a different file.`;
  }
  if (report.verdict === 'warn') {
    return `Your file is ready with ${count === 1 ? '1 thing' : `${formatCount(count)} things`} to check. ${pages}. Confirm to continue.`;
  }
  return `Your file looks good. ${pages}, ${formatDetectedSize(report)}.`;
}
